import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Modal, Platform, RefreshControl, ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import SectionCard from '../components/SectionCard';
import StatCard from '../components/StatCard';
import DateFilterBar from '../components/DateFilterBar';
import DateInput from '../components/DateInput';
import SubExpenseEditor, { normalizeSubExpenses, subExpenseTotal } from '../components/SubExpenseEditor';
import {
  fetchAnalytics,
  fetchCategories,
  fetchCutSuggestions,
  deleteExpensePlan,
  markExpensePlanDone,
  postCutSuggestion,
  postCutSuggestionGroup,
  postExpensePlan,
  postFundPartition,
  postRecurringExpense,
  postSavingsPlan,
  postSavingsRecord,
  recordRecurringExpense,
  updateRecurringExpense,
  updateExpensePlan,
  updateSavingsPlan,
} from '../lib/api';
import { currency, percent, todayKey } from '../lib/format';

const frequencies = ['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'];
const savingsFrequencies = ['daily', 'weekly', 'monthly'];
const urgencyLevels = [
  { key: 'critical', label: 'Critical', color: 'bg-rose-700', text: 'text-white' },
  { key: 'high', label: 'High', color: 'bg-orange-600', text: 'text-white' },
  { key: 'medium', label: 'Medium', color: 'bg-amber-100', text: 'text-amber-800' },
  { key: 'low', label: 'Low', color: 'bg-slate-100', text: 'text-slate-700' },
];
const importanceLevels = [
  { key: 'essential', label: 'Essential' },
  { key: 'important', label: 'Important' },
  { key: 'normal', label: 'Normal' },
  { key: 'optional', label: 'Optional' },
];
const recurringPageSize = 6;
const blankPlan = () => ({ name: '', target_amount: '', start_date: todayKey(), target_date: '', category: 'General', contribution_frequency: 'monthly', strict: false, status: 'active' });
const blankRecurring = () => ({ category: 'Bills', item: '', amount: '', frequency: 'monthly', next_due_date: todayKey(), notes: '', active: true, sub_expenses: [] });
const blankExpensePlan = (planType = 'expense') => ({ category: 'Bills', item: '', planned_amount: '', due_date: todayKey(), urgency: 'medium', importance: 'normal', plan_type: planType, notes: '', sub_expenses: [] });

export default function PlanningScreen({ route, navigation }) {
  const scrollRef = useRef(null);
  const sectionY = useRef({});
  const [analytics, setAnalytics] = useState(null);
  const [cutGroups, setCutGroups] = useState([]);
  const [categories, setCategories] = useState({ expense: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [filters, setFilters] = useState({ start: '', end: '' });
  const [appliedFilters, setAppliedFilters] = useState({ start: '', end: '' });

  const [plan, setPlan] = useState(blankPlan());
  const [editingPlan, setEditingPlan] = useState(null);
  const [record, setRecord] = useState({ plan_id: '', amount: '', date: todayKey(), notes: '' });
  const [recurring, setRecurring] = useState(blankRecurring());
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringPage, setRecurringPage] = useState(1);
  const [group, setGroup] = useState({ name: '', target_category: 'Bills', monthly_target: '' });
  const [suggestion, setSuggestion] = useState({ group_id: '', title: '', detail: '', estimated_savings: '' });
  const [expensePlan, setExpensePlan] = useState(blankExpensePlan());
  const [editingExpensePlan, setEditingExpensePlan] = useState(null);
  const [partition, setPartition] = useState({ name: '', category: 'Transport', allocated_amount: '', start_date: todayKey(), end_date: '', notes: '' });

  const showMessage = (title, message) => {
    if (Platform.OS === 'web') window.alert(`${title}\n\n${message}`);
    else Alert.alert(title, message);
  };

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const [analyticsData, groups, categoryData] = await Promise.all([fetchAnalytics(appliedFilters), fetchCutSuggestions(), fetchCategories()]);
      setAnalytics(analyticsData);
      setCutGroups(groups);
      setCategories(categoryData);
      if (!suggestion.group_id && groups[0]?.id) setSuggestion((current) => ({ ...current, group_id: String(groups[0].id) }));
    } catch (err) {
      showMessage('Could not load planning data', err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [suggestion.group_id, appliedFilters]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useFocusEffect(useCallback(() => {
    load(false);
  }, [load]));

  useEffect(() => {
    const focus = route?.params?.focus;
    if (!focus || loading) return;
    const timer = setTimeout(() => {
      scrollRef.current?.scrollTo({ y: Math.max((sectionY.current[focus] || 0) - 12, 0), animated: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [route?.params?.focus, loading]);

  useEffect(() => {
    if (loading || !route?.params?.openModal) return;
    if (route.params.planId) setRecord((current) => ({ ...current, plan_id: String(route.params.planId) }));
    setActiveModal(route.params.openModal);
    navigation.setParams({ openModal: undefined, planId: undefined });
  }, [loading, navigation, route?.params?.openModal, route?.params?.planId]);

  const submit = async (action) => {
    setSaving(true);
    try {
      await action();
      await load(true);
    } catch (err) {
      showMessage('Could not save', err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCreateGoal = () => {
    setEditingPlan(null);
    setPlan(blankPlan());
    setActiveModal('goal');
  };

  const openEditGoal = (item) => {
    setEditingPlan(item);
    setPlan({
      name: item.name || '',
      target_amount: String(item.target_amount || ''),
      start_date: item.start_date || todayKey(),
      target_date: item.target_date || '',
      category: item.category || 'General',
      contribution_frequency: item.contribution_frequency || 'monthly',
      strict: Boolean(item.strict),
      status: item.status || 'active',
    });
    setActiveModal('goal');
  };

  const openCreateRecurring = () => {
    setEditingRecurring(null);
    setRecurring(blankRecurring());
    setActiveModal('recurring');
  };

  const openEditRecurring = (item) => {
    setEditingRecurring(item);
    setRecurring({
      category: item.category || 'Bills',
      item: item.item || '',
      amount: String(item.amount || ''),
      frequency: item.frequency || 'monthly',
      next_due_date: item.next_due_date || todayKey(),
      payment_method: item.payment_method || '',
      notes: item.notes || '',
      active: item.active !== false,
      sub_expenses: normalizeSubExpenses(item.sub_expenses || []),
    });
    setActiveModal('recurring');
  };

  const openEditExpensePlan = (item) => {
    setEditingExpensePlan(item);
    setExpensePlan({
      category: item.category || 'Bills',
      item: item.item || '',
      planned_amount: String(item.planned_amount || ''),
      due_date: item.due_date || todayKey(),
      urgency: item.urgency || 'medium',
      importance: item.importance || 'normal',
      plan_type: item.plan_type || 'expense',
      notes: item.notes || '',
      status: item.status || 'planned',
      sub_expenses: normalizeSubExpenses(item.sub_expenses || []),
    });
    setActiveModal('expensePlan');
  };

  const confirmDeleteExpensePlan = (item) => {
    if (item.status === 'done') return;
    const runDelete = () => submit(() => deleteExpensePlan(item.id));
    if (Platform.OS === 'web') {
      if (window.confirm(`Delete planned expense "${item.item}"?`)) runDelete();
    } else {
      Alert.alert('Delete planned expense?', item.item, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: runDelete },
      ]);
    }
  };

  const setExpensePlanKind = (kind) => {
    setEditingExpensePlan(null);
    setExpensePlan(blankExpensePlan(kind));
  };

  const renderPriorityControls = (state, setState) => (
    <>
      <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Urgency</Text>
      <View className="mb-3 flex-row flex-wrap gap-2">
        {urgencyLevels.map((entry) => (
          <TouchableOpacity
            key={entry.key}
            className={`rounded-full border px-4 py-2 ${state.urgency === entry.key ? entry.color : 'border-slate-200 bg-white'}`}
            onPress={() => setState({ ...state, urgency: entry.key })}
          >
            <Text className={`font-bold ${state.urgency === entry.key ? entry.text : 'text-slate-700'}`}>{entry.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Importance</Text>
      <View className="mb-3 flex-row flex-wrap gap-2">
        {importanceLevels.map((entry) => (
          <TouchableOpacity
            key={entry.key}
            className={`rounded-full border px-4 py-2 ${state.importance === entry.key ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`}
            onPress={() => setState({ ...state, importance: entry.key })}
          >
            <Text className={`font-bold ${state.importance === entry.key ? 'text-white' : 'text-slate-700'}`}>{entry.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </>
  );

  const renderExpenseCard = (item) => {
    const urgency = urgencyLevels.find((entry) => entry.key === item.urgency) || urgencyLevels[2];
    const statusText = item.due_status === 'overdue' ? `${Math.abs(item.days_until_due)} day(s) overdue` : item.due_status === 'due_today' ? 'Due today' : `Due in ${item.days_until_due} day(s)`;
    return (
      <View key={item.id} className="mb-3 rounded-2xl bg-white p-4">
        <View className="flex-row items-start justify-between gap-3">
          <View className="flex-1">
            <Text className="font-black text-slate-950">{item.item}</Text>
            <Text className="mt-1 text-sm text-slate-500">{item.category} - due {item.due_date}</Text>
            <View className="mt-2 flex-row flex-wrap gap-2">
              <View className={`rounded-full px-3 py-1 ${urgency.color}`}>
                <Text className={`text-xs font-black uppercase ${urgency.text}`}>{item.urgency}</Text>
              </View>
              <View className="rounded-full bg-cyan-50 px-3 py-1">
                <Text className="text-xs font-black uppercase text-cyan-700">{item.importance}</Text>
              </View>
              <View className={`rounded-full px-3 py-1 ${item.due_status === 'overdue' ? 'bg-rose-50' : item.due_status === 'due_today' ? 'bg-orange-50' : 'bg-slate-100'}`}>
                <Text className={`text-xs font-black uppercase ${item.due_status === 'overdue' ? 'text-rose-700' : item.due_status === 'due_today' ? 'text-orange-700' : 'text-slate-600'}`}>{statusText}</Text>
              </View>
            </View>
          </View>
          <Text className="font-black text-rose-600">{currency(item.planned_amount)}</Text>
        </View>
        {item.sub_expenses?.length ? (
          <View className="mt-3 rounded-xl bg-slate-50 p-3">
            {item.sub_expenses.slice(0, 4).map((sub, index) => (
              <View key={`${sub.item}-${index}`} className="mb-1 flex-row justify-between gap-3">
                <Text className="flex-1 text-xs font-semibold text-slate-600">{sub.item}</Text>
                <Text className="text-xs font-bold text-slate-700">{currency(sub.amount)}</Text>
              </View>
            ))}
          </View>
        ) : null}
        <View className="mt-3 flex-row items-center gap-2">
          <TouchableOpacity
            className={`h-11 w-11 items-center justify-center rounded-xl ${item.status === 'done' ? 'bg-emerald-700' : 'bg-slate-900'}`}
            accessibilityLabel={item.status === 'done' ? 'Mark planned' : 'Mark done'}
            onPress={() => submit(() => markExpensePlanDone(item.id, item.status === 'done' ? 'planned' : 'done'))}
          >
            <MaterialIcons name={item.status === 'done' ? 'undo' : 'check'} size={22} color="#ffffff" />
          </TouchableOpacity>
          {item.status !== 'done' ? (
            <>
              <TouchableOpacity className="h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white" accessibilityLabel="Edit expense" onPress={() => openEditExpensePlan(item)}>
                <MaterialIcons name="edit" size={21} color="#0f172a" />
              </TouchableOpacity>
              <TouchableOpacity className="h-11 w-11 items-center justify-center rounded-xl bg-rose-50" accessibilityLabel="Delete expense" onPress={() => confirmDeleteExpensePlan(item)}>
                <MaterialIcons name="delete-outline" size={21} color="#e11d48" />
              </TouchableOpacity>
            </>
          ) : null}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center bg-slate-50">
        <ActivityIndicator size="large" color="#4f46e5" />
        <Text className="mt-4 text-slate-600">Loading planning tools...</Text>
      </View>
    );
  }

  const health = analytics?.financialHealth || {};
  const planning = analytics?.planning || {};
  const expensePlanning = planning.expensePlans || { plans: [], forecast: { total: 0, done: 0, pending: 0, byMonth: [] } };
  const expenseRows = (expensePlanning.plans || []).filter((item) => (item.plan_type || 'expense') === 'expense');
  const wishlistRows = (expensePlanning.plans || []).filter((item) => item.plan_type === 'wishlist');
  const urgentExpenseRows = expenseRows.filter((item) => item.status !== 'done' && ['critical', 'high'].includes(item.urgency));
  const overdueExpenseRows = expenseRows.filter((item) => item.status !== 'done' && item.due_status === 'overdue');
  const fundPartitions = planning.fundPartitions || [];
  const plans = health.savingsPlans || [];
  const combinedMonthlyRequired = plans.reduce((sum, item) => sum + Number(item.monthly_equivalent_required ?? item.monthly_required ?? 0), 0);
  const combinedTarget = plans.reduce((sum, item) => sum + Number(item.target_amount || 0), 0);
  const combinedRemaining = plans.reduce((sum, item) => sum + Number(item.remaining_amount || 0), 0);
  const recurringExpenses = health.recurringExpenses || [];
  const recurringTotalPages = Math.max(Math.ceil(recurringExpenses.length / recurringPageSize), 1);
  const recurringRows = recurringExpenses.slice((Math.min(recurringPage, recurringTotalPages) - 1) * recurringPageSize, Math.min(recurringPage, recurringTotalPages) * recurringPageSize);
  const expenseCategories = categories.expense?.length ? categories.expense : ['Bills', 'Food', 'Transport', 'Subscriptions', 'Other'];
  const single = route?.params?.single || null;
  const show = (name) => single === name;

  if (!single) {
    const pages = [
      { title: 'Savings Goals', subtitle: 'Create goals and record deposits.', icon: 'flag', route: 'SavingsGoals', color: 'bg-indigo-700' },
      { title: 'Expenses', subtitle: 'Track due dates, urgency, and forecast.', icon: 'priority-high', route: 'Expenses', color: 'bg-rose-700' },
      { title: 'Wishlist', subtitle: 'Things you want to buy later.', icon: 'redeem', route: 'Wishlist', color: 'bg-fuchsia-700' },
      { title: 'Fund Partitions', subtitle: 'Allocate money into spending buckets.', icon: 'account-tree', route: 'FundPartitions', color: 'bg-cyan-700' },
      { title: 'Recurring Expenses', subtitle: 'Manage repeated bills and subscriptions.', icon: 'event-repeat', route: 'RecurringExpenses', color: 'bg-amber-700' },
      { title: 'Cut Suggestions', subtitle: 'Group ideas for reducing spending.', icon: 'content-cut', route: 'CutSuggestions', color: 'bg-slate-900' },
    ];

    return (
      <ScrollView className="flex-1 bg-slate-50" contentContainerStyle={{ padding: 16, paddingBottom: 36, width: '100%', maxWidth: 1180, alignSelf: 'center' }}>
        <View className="rounded-2xl bg-indigo-950 p-5">
          <Text className="text-sm font-semibold uppercase text-indigo-200">Planning center</Text>
          <Text className="mt-2 text-3xl font-black text-white">Choose what you want to manage.</Text>
          <Text className="mt-3 text-base text-indigo-100">Savings goals, expense forecasts, fund partitions, recurring bills, and expense-cut ideas now live on separate pages.</Text>
        </View>

        <View className="mt-4 flex-row flex-wrap gap-3">
          <View className="min-w-[150px] flex-1">
            <StatCard label="Saved" value={currency(health.savingsTotal)} subtitle="All savings records" icon="savings" tone="green" />
          </View>
          <View className="min-w-[150px] flex-1">
            <StatCard label="Forecast" value={currency(expensePlanning.forecast.total)} subtitle="Planned expenses" icon="event-note" tone="red" />
          </View>
          <View className="min-w-[150px] flex-1">
            <StatCard label="Partitioned" value={currency(fundPartitions.reduce((sum, item) => sum + Number(item.allocated_amount || 0), 0))} subtitle="Allocated funds" icon="account-tree" tone="cyan" />
          </View>
        </View>

        <View className="mt-4">
          {pages.map((page) => (
            <TouchableOpacity key={page.route} className={`mb-3 rounded-2xl p-4 ${page.color}`} onPress={() => navigation.navigate(page.route)}>
              <View className="flex-row items-center justify-between">
                <View className="flex-1 flex-row items-center">
                  <MaterialIcons name={page.icon} size={24} color="#ffffff" />
                  <View className="ml-3 flex-1">
                    <Text className="text-base font-black text-white">{page.title}</Text>
                    <Text className="mt-1 text-sm text-white">{page.subtitle}</Text>
                  </View>
                </View>
                <MaterialIcons name="arrow-forward" size={22} color="#ffffff" />
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      ref={scrollRef}
      className="flex-1 bg-slate-50"
      contentContainerStyle={{ padding: 16, paddingBottom: 36, width: '100%', maxWidth: 1180, alignSelf: 'center' }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
    >
      <View className="rounded-2xl bg-indigo-950 p-5">
        <Text className="text-sm font-semibold uppercase text-indigo-200">Planning center</Text>
        <Text className="mt-2 text-3xl font-black text-white">Plan what you want to achieve.</Text>
        <Text className="mt-3 text-base text-indigo-100">Create achievement goals for things you want to buy, fund, or build toward with a deadline or forever timeline.</Text>
      </View>

      <View className="mt-4 flex-row flex-wrap gap-3">
        <View className="min-w-[150px] flex-1">
          <StatCard label="Saved" value={currency(health.savingsTotal)} subtitle="All savings records" icon="savings" tone="green" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Recurring" value={currency(health.recurringMonthlyEstimate)} subtitle="Estimated monthly" icon="event-repeat" tone="amber" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Goal plans" value={String(plans.length)} subtitle="Things to achieve" icon="flag" tone="indigo" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Forecast" value={currency(expensePlanning.forecast.total)} subtitle="Planned expenses" icon="event-note" tone="red" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Partitioned" value={currency(fundPartitions.reduce((sum, item) => sum + Number(item.allocated_amount || 0), 0))} subtitle="Allocated funds" icon="account-tree" tone="cyan" />
        </View>
      </View>

      <View className="mt-4">
        <DateFilterBar
          filters={filters}
          onChange={setFilters}
          onApply={() => setAppliedFilters(filters)}
          onClear={() => {
            setFilters({ start: '', end: '' });
            setAppliedFilters({ start: '', end: '' });
          }}
        />
      </View>

      <View className="mt-4" style={{ display: show('saving') ? 'flex' : 'none' }} onLayout={(event) => { sectionY.current.saving = event.nativeEvent.layout.y; }}>
        <SectionCard title="Financial Achievement Goals" subtitle="Save toward something you want to achieve, buy, or fund. A blank target date means forever.">
          <View className="mb-4 flex-row flex-wrap gap-3">
            <View className="min-w-[150px] flex-1">
              <StatCard label="Monthly needed" value={currency(combinedMonthlyRequired)} subtitle="For timed goals" icon="calendar-month" tone="indigo" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Total target" value={currency(combinedTarget)} subtitle="All goals" icon="flag" tone="cyan" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Remaining" value={currency(combinedRemaining)} subtitle="Still needed" icon="savings" tone="amber" />
            </View>
          </View>

          <View className="mb-4 flex-row flex-wrap gap-3">
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-indigo-700 px-4 py-3" onPress={openCreateGoal}>
              <Text className="text-center font-bold text-white">Create goal</Text>
            </TouchableOpacity>
            <TouchableOpacity className="min-w-[150px] flex-1 rounded-2xl bg-emerald-700 px-4 py-3" onPress={() => setActiveModal('savingRecord')}>
              <Text className="text-center font-bold text-white">Record savings</Text>
            </TouchableOpacity>
          </View>

          {plans.map((item) => (
            <View key={item.id} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row justify-between gap-3">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">#{item.id} {item.name}</Text>
                  <Text className="mt-1 text-sm text-slate-500">
                    {item.deadline_label} • {currency(item.saved_amount)} of {currency(item.target_amount)}
                  </Text>
                  <Text className="mt-1 text-sm font-semibold text-indigo-700">
                    {item.per_period_required === null ? 'Forever goal - no deadline breakdown' : `${currency(item.per_period_required)} ${item.contribution_frequency} for ${item.periods_remaining} period(s)`}
                  </Text>
                  <Text className="mt-1 text-sm text-slate-500">
                    Monthly equivalent: {item.monthly_equivalent_required === null ? 'N/A' : currency(item.monthly_equivalent_required)} | next due {item.next_due_date}
                  </Text>
                  {item.strict ? <Text className="mt-1 text-sm font-bold text-rose-700">{item.is_due ? 'Strict plan due now' : 'Strict reminders on'}</Text> : null}
                </View>
                <MaterialIcons name={item.achieved ? 'emoji-events' : 'flag'} size={24} color={item.achieved ? '#d97706' : '#4f46e5'} />
              </View>
              <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <View className="h-2 rounded-full bg-indigo-600" style={{ width: `${Math.min(item.progress_percent, 100)}%` }} />
              </View>
              <View className="mt-3 flex-row items-center justify-between gap-3">
                <Text className="flex-1 text-sm font-semibold text-slate-700">{currency(item.remaining_amount)} remaining</Text>
                <TouchableOpacity className="rounded-full bg-slate-900 px-4 py-2" onPress={() => openEditGoal(item)}>
                  <Text className="font-bold text-white">Edit</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
          {!plans.length ? <Text className="text-slate-500">No achievement goal yet. Create one below.</Text> : null}
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('expenses') || show('expensePlan') ? 'flex' : 'none' }} onLayout={(event) => { sectionY.current.expensePlan = event.nativeEvent.layout.y; }}>
        <SectionCard title="Expenses By Urgency" subtitle="All planned expenses ranked by urgency, importance, and due date.">
          <View className="mb-4 flex-row flex-wrap gap-3">
            <View className="min-w-[150px] flex-1">
              <StatCard label="Pending" value={currency(expenseRows.filter((item) => item.status !== 'done').reduce((sum, item) => sum + Number(item.planned_amount || 0), 0))} subtitle="Expense forecast" icon="event-note" tone="red" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Urgent" value={String(urgentExpenseRows.length)} subtitle="High or critical" icon="priority-high" tone="amber" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Overdue" value={String(overdueExpenseRows.length)} subtitle="Past due date" icon="warning" tone="red" />
            </View>
          </View>

          {(expensePlanning.forecast.byMonth || []).map((month) => (
            <View key={month.month} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between">
                <Text className="font-black text-slate-950">{month.month}</Text>
                <Text className="font-black text-rose-600">{currency(month.total)}</Text>
              </View>
              <Text className="mt-1 text-sm text-slate-500">{month.count} planned • {currency(month.pending)} pending</Text>
            </View>
          ))}

          {expenseRows.map(renderExpenseCard)}
          {!expenseRows.length ? <Text className="text-slate-500">No planned expenses yet.</Text> : null}
          {false && (expensePlanning.plans || []).map((item) => (
            <View key={item.id} className="mb-3 rounded-2xl bg-white p-4">
              <View className="flex-row items-center justify-between gap-3">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">{item.item}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{item.category} • due {item.due_date}</Text>
                </View>
                <Text className="font-black text-rose-600">{currency(item.planned_amount)}</Text>
              </View>
              <View className="mt-3 flex-row items-center gap-2">
                <TouchableOpacity
                  className={`h-11 w-11 items-center justify-center rounded-xl ${item.status === 'done' ? 'bg-emerald-700' : 'bg-slate-900'}`}
                  accessibilityLabel={item.status === 'done' ? 'Mark planned' : 'Mark done'}
                  onPress={() => submit(() => markExpensePlanDone(item.id, item.status === 'done' ? 'planned' : 'done'))}
                >
                  <MaterialIcons name={item.status === 'done' ? 'undo' : 'check'} size={22} color="#ffffff" />
                </TouchableOpacity>
                {item.status !== 'done' ? (
                  <>
                    <TouchableOpacity
                      className="h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white"
                      accessibilityLabel="Edit planned expense"
                      onPress={() => openEditExpensePlan(item)}
                    >
                      <MaterialIcons name="edit" size={21} color="#0f172a" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      className="h-11 w-11 items-center justify-center rounded-xl bg-rose-50"
                      accessibilityLabel="Delete planned expense"
                      onPress={() => confirmDeleteExpensePlan(item)}
                    >
                      <MaterialIcons name="delete-outline" size={21} color="#e11d48" />
                    </TouchableOpacity>
                  </>
                ) : null}
              </View>
            </View>
          ))}

          <View className="mt-2 border-t border-slate-100 pt-4">
            <Text className="mb-3 font-black text-slate-950">Add planned expense</Text>
            <View className="mb-3 flex-row flex-wrap gap-2">
              {expenseCategories.slice(0, 8).map((entry) => (
                <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${expensePlan.category === entry ? 'border-rose-700 bg-rose-700' : 'border-slate-200 bg-white'}`} onPress={() => setExpensePlan({ ...expensePlan, category: entry })}>
                  <Text className={`font-semibold ${expensePlan.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Expense to cover, e.g. House rent" placeholderTextColor="#94a3b8" value={expensePlan.item} onChangeText={(value) => setExpensePlan({ ...expensePlan, item: value })} />
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Forecast amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={expensePlan.planned_amount} onChangeText={(value) => setExpensePlan({ ...expensePlan, planned_amount: value })} />
            {renderPriorityControls(expensePlan, setExpensePlan)}
            <View className="mb-3">
              <SubExpenseEditor
                rows={expensePlan.sub_expenses || []}
                onChange={(rows) => {
                  const subRows = normalizeSubExpenses(rows);
                  const total = subExpenseTotal(subRows);
                  setExpensePlan({ ...expensePlan, sub_expenses: rows, planned_amount: total > 0 ? String(total) : expensePlan.planned_amount });
                }}
                title="Planned sub-expenses"
                subtitle="Break the planned expense into smaller expected items."
              />
            </View>
            <View className="mb-3">
              <DateInput value={expensePlan.due_date} onChangeText={(value) => setExpensePlan({ ...expensePlan, due_date: value })} placeholder="Due date" />
            </View>
            <TouchableOpacity className="rounded-2xl bg-rose-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
              await postExpensePlan({ ...expensePlan, plan_type: 'expense', sub_expenses: normalizeSubExpenses(expensePlan.sub_expenses || []) });
              setExpensePlan(blankExpensePlan('expense'));
            })}>
              <Text className="text-center font-bold text-white">Save planned expense</Text>
            </TouchableOpacity>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('wishlist') ? 'flex' : 'none' }}>
        <SectionCard title="Wishlist" subtitle="Things you want to buy later, with urgency, due dates, and sub-items.">
          <View className="mb-4 flex-row flex-wrap gap-3">
            <View className="min-w-[150px] flex-1">
              <StatCard label="Wishlist total" value={currency(wishlistRows.reduce((sum, item) => sum + Number(item.planned_amount || 0), 0))} subtitle="All wanted items" icon="redeem" tone="indigo" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Pending" value={String(wishlistRows.filter((item) => item.status !== 'done').length)} subtitle="Not bought yet" icon="shopping-bag" tone="amber" />
            </View>
          </View>
          {wishlistRows.map(renderExpenseCard)}
          {!wishlistRows.length ? <Text className="text-slate-500">No wishlist item yet.</Text> : null}

          <View className="mt-2 border-t border-slate-100 pt-4">
            <Text className="mb-3 font-black text-slate-950">Add wishlist item</Text>
            <View className="mb-3 flex-row flex-wrap gap-2">
              {expenseCategories.slice(0, 8).map((entry) => (
                <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${expensePlan.category === entry ? 'border-fuchsia-700 bg-fuchsia-700' : 'border-slate-200 bg-white'}`} onPress={() => setExpensePlan({ ...expensePlan, category: entry, plan_type: 'wishlist' })}>
                  <Text className={`font-semibold ${expensePlan.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Wishlist item, e.g. New laptop" placeholderTextColor="#94a3b8" value={expensePlan.item} onChangeText={(value) => setExpensePlan({ ...expensePlan, item: value, plan_type: 'wishlist' })} />
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Expected amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={expensePlan.planned_amount} onChangeText={(value) => setExpensePlan({ ...expensePlan, planned_amount: value })} />
            {renderPriorityControls(expensePlan, setExpensePlan)}
            <View className="mb-3">
              <SubExpenseEditor
                rows={expensePlan.sub_expenses || []}
                onChange={(rows) => {
                  const subRows = normalizeSubExpenses(rows);
                  const total = subExpenseTotal(subRows);
                  setExpensePlan({ ...expensePlan, sub_expenses: rows, planned_amount: total > 0 ? String(total) : expensePlan.planned_amount });
                }}
                title="Wishlist sub-items"
                subtitle="Break this item into smaller things you want."
              />
            </View>
            <View className="mb-3">
              <DateInput value={expensePlan.due_date} onChangeText={(value) => setExpensePlan({ ...expensePlan, due_date: value })} placeholder="Target date" />
            </View>
            <TouchableOpacity className="rounded-2xl bg-fuchsia-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
              await postExpensePlan({ ...expensePlan, plan_type: 'wishlist', sub_expenses: normalizeSubExpenses(expensePlan.sub_expenses || []) });
              setExpensePlan(blankExpensePlan('wishlist'));
            })}>
              <Text className="text-center font-bold text-white">Save wishlist item</Text>
            </TouchableOpacity>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('partition') ? 'flex' : 'none' }} onLayout={(event) => { sectionY.current.partition = event.nativeEvent.layout.y; }}>
        <SectionCard title="Fund Partitions" subtitle="Allocate money into spending buckets. When recording an expense, choose which bucket it comes from.">
          {fundPartitions.map((item) => (
            <View key={item.id} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between gap-3">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">{item.name}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{item.category || 'General'} • {currency(item.spent_amount)} spent</Text>
                </View>
                <Text className={`font-black ${item.remaining_amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{currency(item.remaining_amount)}</Text>
              </View>
              <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <View className={`h-2 rounded-full ${item.used_percent > 100 ? 'bg-rose-600' : 'bg-cyan-600'}`} style={{ width: `${Math.min(item.used_percent, 100)}%` }} />
              </View>
              <Text className="mt-2 text-sm text-slate-600">{percent(item.used_percent)} used of {currency(item.allocated_amount)}</Text>
            </View>
          ))}
          {!fundPartitions.length ? <Text className="mb-3 text-slate-500">No fund partitions yet.</Text> : null}

          <View className="mt-2 border-t border-slate-100 pt-4">
            <Text className="mb-3 font-black text-slate-950">Create fund partition</Text>
            <View className="mb-3 flex-row flex-wrap gap-2">
              {expenseCategories.slice(0, 8).map((entry) => (
                <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${partition.category === entry ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`} onPress={() => setPartition({ ...partition, category: entry, name: partition.name || entry })}>
                  <Text className={`font-semibold ${partition.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Partition name, e.g. Transportation" placeholderTextColor="#94a3b8" value={partition.name} onChangeText={(value) => setPartition({ ...partition, name: value })} />
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Allocated amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={partition.allocated_amount} onChangeText={(value) => setPartition({ ...partition, allocated_amount: value })} />
            <View className="mb-3">
              <DateInput value={partition.start_date} onChangeText={(value) => setPartition({ ...partition, start_date: value })} placeholder="Start date" />
            </View>
            <View className="mb-3">
              <DateInput value={partition.end_date} onChangeText={(value) => setPartition({ ...partition, end_date: value })} placeholder="End date, optional" />
            </View>
            <TouchableOpacity className="rounded-2xl bg-cyan-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
              await postFundPartition(partition);
              setPartition({ name: '', category: 'Transport', allocated_amount: '', start_date: todayKey(), end_date: '', notes: '' });
            })}>
              <Text className="text-center font-bold text-white">Create partition</Text>
            </TouchableOpacity>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('milestones') ? 'flex' : 'none' }}>
        <SectionCard title="Money Milestones" subtitle="Automatic behavior milestones based on your ledger and savings records.">
          {(health.achievements || []).map((item) => (
            <View key={item.key} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between">
                <Text className="flex-1 font-black text-slate-950">{item.title}</Text>
                <MaterialIcons name={item.achieved ? 'emoji-events' : 'lock-outline'} size={24} color={item.achieved ? '#d97706' : '#94a3b8'} />
              </View>
              <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <View className="h-2 rounded-full bg-indigo-600" style={{ width: `${Math.min((Number(item.progress || 0) / Math.max(Number(item.target || 1), 1)) * 100, 100)}%` }} />
              </View>
            </View>
          ))}
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: 'none' }}>
        <SectionCard title="Create Achievement Goal" subtitle="Examples: buy laptop, emergency fund, rent buffer, school fees, house project, travel fund.">
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="What do you want to achieve or buy?" placeholderTextColor="#94a3b8" value={plan.name} onChangeText={(value) => setPlan({ ...plan, name: value })} />
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Amount needed" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={plan.target_amount} onChangeText={(value) => setPlan({ ...plan, target_amount: value })} />
          <View className="mb-3">
            <DateInput value={plan.target_date} onChangeText={(value) => setPlan({ ...plan, target_date: value })} placeholder="Target date, optional" />
          </View>
          <TouchableOpacity className="rounded-2xl bg-indigo-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
            await postSavingsPlan(plan);
            setPlan(blankPlan());
          })}>
            <Text className="text-center font-bold text-white">Save achievement goal</Text>
          </TouchableOpacity>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: 'none' }}>
        <SectionCard title="Goal Savings Records" subtitle="Record money saved toward an achievement goal. Use the goal ID shown above.">
          {plans.map((item) => (
            <View key={item.id} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row justify-between gap-3">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">#{item.id} {item.name}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{item.deadline_label} • {currency(item.saved_amount)} of {currency(item.target_amount)} saved</Text>
                </View>
                <Text className="font-bold text-indigo-700">{percent(item.progress_percent)}</Text>
              </View>
              <View className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <View className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.min(item.progress_percent, 100)}%` }} />
              </View>
            </View>
          ))}
          {!plans.length ? <Text className="mb-3 text-slate-500">No savings plan yet.</Text> : null}

          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-950" placeholder="Plan ID, optional" placeholderTextColor="#94a3b8" keyboardType="number-pad" value={record.plan_id} onChangeText={(value) => setRecord({ ...record, plan_id: value })} />
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-950" placeholder="Saved amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={record.amount} onChangeText={(value) => setRecord({ ...record, amount: value })} />
          <View className="mb-3">
            <DateInput value={record.date} onChangeText={(value) => setRecord({ ...record, date: value })} placeholder="Date" />
          </View>
          <TouchableOpacity className="rounded-2xl bg-emerald-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
            await postSavingsRecord({ ...record, plan_id: record.plan_id || null });
            setRecord({ plan_id: '', amount: '', date: todayKey(), notes: '' });
          })}>
            <Text className="text-center font-bold text-white">Record savings</Text>
          </TouchableOpacity>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('recurring') ? 'flex' : 'none' }}>
        <SectionCard title="Recurring Expenses" subtitle="Track rent, subscriptions, data, loan repayments, and bills.">
          <View className="mb-4 flex-row flex-wrap gap-3">
            <View className="min-w-[150px] flex-1">
              <StatCard label="Monthly recurring" value={currency(health.recurringMonthlyEstimate)} subtitle="Full monthly obligation" icon="event-repeat" tone="amber" />
            </View>
            <View className="min-w-[150px] flex-1">
              <StatCard label="Due in filter" value={String(recurringExpenses.length)} subtitle="Recurring item(s)" icon="receipt-long" tone="cyan" />
            </View>
          </View>
          <TouchableOpacity className="mb-4 rounded-2xl bg-amber-700 px-4 py-3" onPress={openCreateRecurring}>
            <Text className="text-center font-bold text-white">Create recurring expense</Text>
          </TouchableOpacity>
          <View style={{ display: 'none' }}>
          <View className="mb-3 flex-row flex-wrap gap-2">
            {expenseCategories.slice(0, 8).map((entry) => (
              <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${recurring.category === entry ? 'border-indigo-700 bg-indigo-700' : 'border-slate-200 bg-white'}`} onPress={() => setRecurring({ ...recurring, category: entry })}>
                <Text className={`font-semibold ${recurring.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Expense name, e.g. Internet data" placeholderTextColor="#94a3b8" value={recurring.item} onChangeText={(value) => setRecurring({ ...recurring, item: value })} />
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={recurring.amount} onChangeText={(value) => setRecurring({ ...recurring, amount: value })} />
          <View className="mb-3 flex-row flex-wrap gap-2">
            {frequencies.map((entry) => (
              <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${recurring.frequency === entry ? 'border-slate-900 bg-slate-900' : 'border-slate-200 bg-white'}`} onPress={() => setRecurring({ ...recurring, frequency: entry })}>
                <Text className={`font-semibold capitalize ${recurring.frequency === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View className="mb-3">
            <DateInput value={recurring.next_due_date} onChangeText={(value) => setRecurring({ ...recurring, next_due_date: value })} placeholder="Next due date" />
          </View>
          <TouchableOpacity className="rounded-2xl bg-amber-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
            await postRecurringExpense(recurring);
            setRecurring({ category: 'Bills', item: '', amount: '', frequency: 'monthly', next_due_date: todayKey(), notes: '' });
          })}>
            <Text className="text-center font-bold text-white">Save recurring expense</Text>
          </TouchableOpacity>
          </View>

          <View className="mt-4">
            {recurringRows.map((item) => (
              <View key={item.id} className="mb-3 rounded-2xl bg-white p-4">
                <View className="flex-row items-center justify-between gap-3">
                  <View className="flex-1">
                    <Text className="font-black text-slate-950">{item.item}</Text>
                    <Text className="mt-1 text-sm text-slate-500">{item.category} • {item.frequency} • due {item.next_due_date}</Text>
                  </View>
                  <Text className="font-black text-rose-600">{currency(item.amount)}</Text>
                </View>
                <View className="mt-3 flex-row flex-wrap gap-2">
                  <TouchableOpacity className="min-w-[130px] flex-1 rounded-xl bg-slate-900 px-4 py-2" onPress={() => submit(() => recordRecurringExpense(item.id))}>
                    <Text className="text-center font-bold text-white">Record as paid</Text>
                  </TouchableOpacity>
                  <TouchableOpacity className="min-w-[100px] flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2" onPress={() => openEditRecurring(item)}>
                    <Text className="text-center font-bold text-slate-800">Edit</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
            {!recurringExpenses.length ? <Text className="text-slate-500">No recurring expenses in this date range.</Text> : null}
            {recurringExpenses.length > recurringPageSize ? (
              <View className="mt-2 flex-row items-center justify-between">
                <TouchableOpacity className={`rounded-xl px-4 py-2 ${recurringPage <= 1 ? 'bg-slate-100' : 'bg-slate-900'}`} disabled={recurringPage <= 1} onPress={() => setRecurringPage((page) => Math.max(page - 1, 1))}>
                  <Text className={`font-bold ${recurringPage <= 1 ? 'text-slate-400' : 'text-white'}`}>Previous</Text>
                </TouchableOpacity>
                <Text className="font-bold text-slate-600">Page {Math.min(recurringPage, recurringTotalPages)} of {recurringTotalPages}</Text>
                <TouchableOpacity className={`rounded-xl px-4 py-2 ${recurringPage >= recurringTotalPages ? 'bg-slate-100' : 'bg-slate-900'}`} disabled={recurringPage >= recurringTotalPages} onPress={() => setRecurringPage((page) => Math.min(page + 1, recurringTotalPages))}>
                  <Text className={`font-bold ${recurringPage >= recurringTotalPages ? 'text-slate-400' : 'text-white'}`}>Next</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </SectionCard>
      </View>

      <View className="mt-4" style={{ display: show('cut') ? 'flex' : 'none' }}>
        <SectionCard title="Expense Cut Suggestions" subtitle="Group ideas by spending area, such as Data, Food, Transport, or Subscriptions.">
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="New group name, e.g. Data" placeholderTextColor="#94a3b8" value={group.name} onChangeText={(value) => setGroup({ ...group, name: value })} />
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Monthly cut target" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={group.monthly_target} onChangeText={(value) => setGroup({ ...group, monthly_target: value })} />
          <TouchableOpacity className="mb-4 rounded-2xl bg-cyan-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
            await postCutSuggestionGroup(group);
            setGroup({ name: '', target_category: 'Bills', monthly_target: '' });
          })}>
            <Text className="text-center font-bold text-white">Add suggestion group</Text>
          </TouchableOpacity>

          <View className="mb-3 flex-row flex-wrap gap-2">
            {cutGroups.map((entry) => (
              <TouchableOpacity key={entry.id} className={`rounded-full border px-4 py-2 ${String(entry.id) === String(suggestion.group_id) ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`} onPress={() => setSuggestion({ ...suggestion, group_id: String(entry.id) })}>
                <Text className={`font-semibold ${String(entry.id) === String(suggestion.group_id) ? 'text-white' : 'text-slate-700'}`}>{entry.name}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Suggestion title" placeholderTextColor="#94a3b8" value={suggestion.title} onChangeText={(value) => setSuggestion({ ...suggestion, title: value })} />
          <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Estimated savings" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={suggestion.estimated_savings} onChangeText={(value) => setSuggestion({ ...suggestion, estimated_savings: value })} />
          <TextInput className="mb-3 min-h-[80px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="How will you cut this expense?" placeholderTextColor="#94a3b8" multiline value={suggestion.detail} onChangeText={(value) => setSuggestion({ ...suggestion, detail: value })} />
          <TouchableOpacity className="rounded-2xl bg-slate-900 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
            await postCutSuggestion(suggestion);
            setSuggestion({ group_id: suggestion.group_id, title: '', detail: '', estimated_savings: '' });
          })}>
            <Text className="text-center font-bold text-white">Save cut suggestion</Text>
          </TouchableOpacity>

          <View className="mt-4">
            {cutGroups.map((entry) => (
              <View key={entry.id} className="mb-3 rounded-2xl bg-white p-4">
                <Text className="font-black text-slate-950">{entry.name}</Text>
                <Text className="mt-1 text-sm text-slate-500">Target cut: {currency(entry.monthly_target)} monthly</Text>
                {entry.suggestions.map((item) => (
                  <View key={item.id} className="mt-3 border-t border-slate-100 pt-3">
                    <Text className="font-bold text-slate-900">{item.title} • {currency(item.estimated_savings)}</Text>
                    {item.detail ? <Text className="mt-1 text-sm text-slate-600">{item.detail}</Text> : null}
                  </View>
                ))}
              </View>
            ))}
          </View>
        </SectionCard>
      </View>

      <Modal visible={activeModal === 'goal'} transparent animationType="fade" onRequestClose={() => setActiveModal(null)}>
        <View className="flex-1 justify-center bg-black/40 p-4">
          <View className="rounded-2xl bg-white p-5">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="text-xl font-black text-slate-950">{editingPlan ? 'Edit achievement goal' : 'Create achievement goal'}</Text>
              <TouchableOpacity onPress={() => {
                setActiveModal(null);
                setEditingPlan(null);
              }}>
                <MaterialIcons name="close" size={24} color="#0f172a" />
              </TouchableOpacity>
            </View>
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="What do you want to achieve or buy?" placeholderTextColor="#94a3b8" value={plan.name} onChangeText={(value) => setPlan({ ...plan, name: value })} />
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Amount needed" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={plan.target_amount} onChangeText={(value) => setPlan({ ...plan, target_amount: value })} />
            <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Start date</Text>
            <View className="mb-3">
              <DateInput value={plan.start_date} onChangeText={(value) => setPlan({ ...plan, start_date: value })} placeholder="Start date" />
            </View>
            <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Target date</Text>
            <View className="mb-3">
              <DateInput value={plan.target_date} onChangeText={(value) => setPlan({ ...plan, target_date: value })} placeholder="Target date, optional" />
            </View>
            <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Saving frequency</Text>
            <View className="mb-3 flex-row flex-wrap gap-2">
              {savingsFrequencies.map((item) => (
                <TouchableOpacity
                  key={item}
                  className={`rounded-full border px-4 py-2 ${plan.contribution_frequency === item ? 'border-indigo-700 bg-indigo-700' : 'border-slate-200 bg-white'}`}
                  onPress={() => setPlan({ ...plan, contribution_frequency: item })}
                >
                  <Text className={`font-bold capitalize ${plan.contribution_frequency === item ? 'text-white' : 'text-slate-700'}`}>{item}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              className={`mb-4 flex-row items-center rounded-2xl border px-4 py-3 ${plan.strict ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-slate-50'}`}
              onPress={() => setPlan({ ...plan, strict: !plan.strict })}
            >
              <MaterialIcons name={plan.strict ? 'notifications-active' : 'notifications-none'} size={22} color={plan.strict ? '#be123c' : '#64748b'} />
              <Text className={`ml-2 flex-1 font-bold ${plan.strict ? 'text-rose-700' : 'text-slate-700'}`}>Pin reminder when this saving is due</Text>
            </TouchableOpacity>
            <TouchableOpacity className="rounded-2xl bg-indigo-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
              if (editingPlan) await updateSavingsPlan(editingPlan.id, plan);
              else await postSavingsPlan(plan);
              setPlan(blankPlan());
              setEditingPlan(null);
              setActiveModal(null);
            })}>
              <Text className="text-center font-bold text-white">{editingPlan ? 'Update goal' : 'Save goal'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={activeModal === 'savingRecord'} transparent animationType="fade" onRequestClose={() => setActiveModal(null)}>
        <View className="flex-1 justify-center bg-black/40 p-4">
          <View className="rounded-2xl bg-white p-5">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="text-xl font-black text-slate-950">Record savings</Text>
              <TouchableOpacity onPress={() => setActiveModal(null)}>
                <MaterialIcons name="close" size={24} color="#0f172a" />
              </TouchableOpacity>
            </View>
            <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Select goal</Text>
            <View className="mb-3 max-h-[220px]">
              {plans.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  className={`mb-2 rounded-2xl border px-4 py-3 ${String(record.plan_id) === String(item.id) ? 'border-emerald-700 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                  onPress={() => setRecord({ ...record, plan_id: String(item.id) })}
                >
                  <Text className="font-bold text-slate-950">{item.name}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{currency(item.remaining_amount)} remaining</Text>
                </TouchableOpacity>
              ))}
              {!plans.length ? <Text className="text-slate-500">Create a goal first.</Text> : null}
            </View>
            <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Saved amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={record.amount} onChangeText={(value) => setRecord({ ...record, amount: value })} />
            <DateInput value={record.date} onChangeText={(value) => setRecord({ ...record, date: value })} placeholder="Date" />
            <TouchableOpacity className="rounded-2xl bg-emerald-700 px-4 py-3" disabled={saving || !plans.length} onPress={() => submit(async () => {
              await postSavingsRecord({ ...record, plan_id: record.plan_id || null });
              setRecord({ plan_id: '', amount: '', date: todayKey(), notes: '' });
              setActiveModal(null);
            })}>
              <Text className="text-center font-bold text-white">Record savings</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={activeModal === 'recurring'} transparent animationType="fade" onRequestClose={() => setActiveModal(null)}>
        <View className="flex-1 justify-center bg-black/40 p-4">
          <View className="max-h-[90%] rounded-2xl bg-white p-5">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="text-xl font-black text-slate-950">{editingRecurring ? 'Edit recurring expense' : 'Create recurring expense'}</Text>
              <TouchableOpacity onPress={() => {
                setActiveModal(null);
                setEditingRecurring(null);
              }}>
                <MaterialIcons name="close" size={24} color="#0f172a" />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Category</Text>
              <View className="mb-3 flex-row flex-wrap gap-2">
                {expenseCategories.slice(0, 10).map((entry) => (
                  <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${recurring.category === entry ? 'border-amber-700 bg-amber-700' : 'border-slate-200 bg-white'}`} onPress={() => setRecurring({ ...recurring, category: entry })}>
                    <Text className={`font-semibold ${recurring.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Expense name, e.g. Internet data" placeholderTextColor="#94a3b8" value={recurring.item} onChangeText={(value) => setRecurring({ ...recurring, item: value })} />
              <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Full amount you pay for this frequency" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={recurring.amount} onChangeText={(value) => setRecurring({ ...recurring, amount: value })} />
              <View className="mb-3">
                <SubExpenseEditor
                  rows={recurring.sub_expenses || []}
                  onChange={(rows) => {
                    const subRows = normalizeSubExpenses(rows);
                    const total = subExpenseTotal(subRows);
                    setRecurring({ ...recurring, sub_expenses: rows, amount: total > 0 ? String(total) : recurring.amount });
                  }}
                  title="Recurring sub-expenses"
                  subtitle="Break this recurring bill into the items it usually contains."
                />
              </View>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Frequency</Text>
              <View className="mb-3 flex-row flex-wrap gap-2">
                {frequencies.map((entry) => (
                  <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${recurring.frequency === entry ? 'border-slate-900 bg-slate-900' : 'border-slate-200 bg-white'}`} onPress={() => setRecurring({ ...recurring, frequency: entry })}>
                    <Text className={`font-semibold capitalize ${recurring.frequency === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Next due date</Text>
              <View className="mb-3">
                <DateInput value={recurring.next_due_date} onChangeText={(value) => setRecurring({ ...recurring, next_due_date: value })} placeholder="Next due date" />
              </View>
              <TouchableOpacity
                className={`mb-4 flex-row items-center rounded-2xl border px-4 py-3 ${recurring.active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}
                onPress={() => setRecurring({ ...recurring, active: !recurring.active })}
              >
                <MaterialIcons name={recurring.active ? 'toggle-on' : 'toggle-off'} size={24} color={recurring.active ? '#047857' : '#64748b'} />
                <Text className={`ml-2 font-bold ${recurring.active ? 'text-emerald-700' : 'text-slate-700'}`}>{recurring.active ? 'Active recurring expense' : 'Inactive recurring expense'}</Text>
              </TouchableOpacity>
              <TouchableOpacity className="rounded-2xl bg-amber-700 px-4 py-3" disabled={saving} onPress={() => submit(async () => {
                const payload = { ...recurring, sub_expenses: normalizeSubExpenses(recurring.sub_expenses || []) };
                if (editingRecurring) await updateRecurringExpense(editingRecurring.id, payload);
                else await postRecurringExpense(payload);
                setRecurring(blankRecurring());
                setEditingRecurring(null);
                setActiveModal(null);
              })}>
                <Text className="text-center font-bold text-white">{editingRecurring ? 'Update recurring expense' : 'Save recurring expense'}</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal visible={activeModal === 'expensePlan'} transparent animationType="fade" onRequestClose={() => setActiveModal(null)}>
        <View className="flex-1 justify-center bg-black/40 p-4">
          <View className="max-h-[90%] rounded-2xl bg-white p-5">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="text-xl font-black text-slate-950">{expensePlan.plan_type === 'wishlist' ? 'Edit wishlist item' : 'Edit planned expense'}</Text>
              <TouchableOpacity onPress={() => {
                setActiveModal(null);
                setEditingExpensePlan(null);
                setExpensePlan(blankExpensePlan());
              }}>
                <MaterialIcons name="close" size={24} color="#0f172a" />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Category</Text>
              <View className="mb-3 flex-row flex-wrap gap-2">
                {expenseCategories.slice(0, 10).map((entry) => (
                  <TouchableOpacity key={entry} className={`rounded-full border px-4 py-2 ${expensePlan.category === entry ? 'border-rose-700 bg-rose-700' : 'border-slate-200 bg-white'}`} onPress={() => setExpensePlan({ ...expensePlan, category: entry })}>
                    <Text className={`font-semibold ${expensePlan.category === entry ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Expense to cover" placeholderTextColor="#94a3b8" value={expensePlan.item} onChangeText={(value) => setExpensePlan({ ...expensePlan, item: value })} />
              <TextInput className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950" placeholder="Forecast amount" placeholderTextColor="#94a3b8" keyboardType="decimal-pad" value={expensePlan.planned_amount} onChangeText={(value) => setExpensePlan({ ...expensePlan, planned_amount: value })} />
              {renderPriorityControls(expensePlan, setExpensePlan)}
              <View className="mb-3">
                <SubExpenseEditor
                  rows={expensePlan.sub_expenses || []}
                  onChange={(rows) => {
                    const subRows = normalizeSubExpenses(rows);
                    const total = subExpenseTotal(subRows);
                    setExpensePlan({ ...expensePlan, sub_expenses: rows, planned_amount: total > 0 ? String(total) : expensePlan.planned_amount });
                  }}
                  title="Planned sub-expenses"
                  subtitle="Update the smaller items inside this planned expense."
                />
              </View>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Due date</Text>
              <View className="mb-4">
                <DateInput value={expensePlan.due_date} onChangeText={(value) => setExpensePlan({ ...expensePlan, due_date: value })} placeholder="Due date" />
              </View>
              <TouchableOpacity className="rounded-2xl bg-rose-700 px-4 py-3" disabled={saving || !editingExpensePlan} onPress={() => submit(async () => {
                await updateExpensePlan(editingExpensePlan.id, { ...expensePlan, sub_expenses: normalizeSubExpenses(expensePlan.sub_expenses || []) });
                setEditingExpensePlan(null);
                setExpensePlan(blankExpensePlan());
                setActiveModal(null);
              })}>
                <Text className="text-center font-bold text-white">Update planned expense</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}
